import React from 'react'
import clsx from 'clsx'
import { ClickAwayListener, Fade, Menu, MenuItem } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { ArrowDown2 } from 'iconsax-react'
import PhoneFlag from '../../../../atoms/PhoneFlag'

import { getFormatted } from '@SonarCX/utilities/phoneNumber'
import {
  selectChatBox,
  updateValue,
  setChannelValue,
  addAlert,
  clearUniqueAlerts
} from '@SonarCX/slices/chatBoxSlice'
import { useCanSend } from '@SonarCX/providers/CanSendProvider'
import { useOwner } from '@SonarCX/providers/OwnerProvider'
import useRowStyles from '../ExtraInputRow/styles'
import useStyles from './styles'

const NumberFromID = ({ id, phones, classes }) => {
  if (!(id in phones)) {
    return null
  }

  const phone = phones[id].channel.value
  return (
    <div className={classes.root}>
      <PhoneFlag value={phone} className={classes.flag} />

      {getFormatted(phone)}
    </div>
  )
}

const SMSInput = () => {
  const dispatch = useAppDispatch()
  const { currentID, values } = useAppSelector(selectChatBox)
  const [channelsMenuOpen, setChannelsMenuOpen] = React.useState(false)
  const { setCanSend } = useCanSend()
  const { owner } = useOwner()
  const rootRef = React.useRef(null)
  const [phones, allPhones, loaded] = React.useMemo(() => {
    return [values.phones ?? [], values.allPhones ?? {}, Boolean(currentID)]
  }, [currentID, values])
  const allVerifiedPhones = React.useMemo(
    () =>
      Object.values(allPhones).filter(phones => {
        return (
          phones.channel.compliance &&
          !phones.channel.deleted_at &&
          phones.channel.validated
        )
      }),
    [allPhones]
  )

  const rowClasses = useRowStyles()
  const classes = useStyles()

  React.useEffect(() => {
    if (!loaded) {
      return
    }

    if (Object.values(allPhones).length === 0) {
      dispatch(
        addAlert({
          message: (
            <>
              <span className='bold'>{owner?.name}</span> has no phone numbers,
              please add one.
            </>
          ),
          unique: 'phone-none'
        })
      )
      dispatch(updateValue('phones', []))
      dispatch(updateValue('phoneSet', false))
      return
    }

    dispatch(clearUniqueAlerts('phone-none'))

    if (phones.length !== 0) {
      return
    }

    if (!values.phoneSet) {
      const id = Object.values(allPhones)[0].channel.id
      dispatch(setChannelValue({ isPhone: true, id }))
    }
  }, [loaded, allPhones, phones.length, values.phoneSet, dispatch, owner])

  // Update canSend
  React.useEffect(() => {
    setCanSend(allVerifiedPhones.length > 0)

    if (!allVerifiedPhones.length === 0) {
      const singular = phones.length === 1
      const message = `${
        singular
          ? 'Selected phone number'
          : 'One or more of the selected phone numbers'
      } has not been validated or has been opted-out by the user.`
      dispatch(
        addAlert({
          message,
          unique: 'phone-invalid'
        })
      )
    } else {
      dispatch(clearUniqueAlerts('phone-invalid'))
    }
  }, [allVerifiedPhones, dispatch, phones.length, setCanSend])

  return (
    <>
      <div className={rowClasses.root}>
        <div className={rowClasses.label}>To</div>

        <div ref={rootRef} className={rowClasses.row}>
          {phones.length === 0 ? (
            <div className={clsx(rowClasses.button, rowClasses.dropdown)}>
              {allVerifiedPhones.length === 0
                ? 'No channels'
                : 'Select phone number'}
            </div>
          ) : (
            <div className={clsx(rowClasses.button, rowClasses.dropdown)}>
              <NumberFromID
                classes={classes}
                phones={allPhones}
                id={phones[0]}
              />
              {allVerifiedPhones.length > 1 && (
                <ArrowDown2
                  variant='Bold'
                  onClick={() => setChannelsMenuOpen(true)}
                  className={classes.dropdown}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {channelsMenuOpen && (
        <ClickAwayListener onClickAway={() => setChannelsMenuOpen(false)}>
          <Menu
            onClose={() => setChannelsMenuOpen(false)}
            open={channelsMenuOpen}
            disableEnforceFocus
            anchorEl={rootRef.current}
            anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'center', vertical: 4 }}
            classes={{ paper: classes.resultsContainer }}
            TransitionComponent={Fade}
          >
            <label className={classes.extraChannelsLabel}>
              Select phone number
            </label>
            <div className={classes.extraChannelsResults}>
              {allVerifiedPhones.map(({ user = {}, channel = {} }) => {
                return (
                  <MenuItem
                    key={`${channel.id}-sms-pill`}
                    component='div'
                    className={classes.result}
                    onClick={() => {
                      dispatch(
                        setChannelValue({ isPhone: true, id: channel.id })
                      )
                      setChannelsMenuOpen(false)
                    }}
                    disableGutters
                  >
                    <div>
                      <label className={classes.smsResult}>{user.name}</label>
                      <PhoneFlag
                        value={channel.value}
                        className={classes.flagDropdown}
                      />
                      <label className={classes.smsValue}>
                        {getFormatted(channel.value)}
                      </label>
                    </div>
                  </MenuItem>
                )
              })}
            </div>
          </Menu>
        </ClickAwayListener>
      )}
    </>
  )
}

export default SMSInput
